export interface PriceTagOcrDetails {
  text: string;
  confidence?: number;
  words?: Array<{ text: string; confidence: number; bbox: { x0: number; y0: number; x1: number; y1: number } }>;
}

type TesseractWorker = {
  recognize: (image: HTMLCanvasElement) => Promise<{ data: PriceTagOcrDetails }>;
};

let workerPromise: Promise<TesseractWorker> | null = null;

async function getWorker(): Promise<TesseractWorker> {
  if (!workerPromise) {
    // v5 can leave createWorker pending on language-load/initialization errors.
    // Surface the worker error and allow a later scan to retry initialization.
    workerPromise = new Promise<TesseractWorker>((resolve, reject) => {
      void import("tesseract.js").then(({ createWorker }) =>
        createWorker("est+eng", 1, { errorHandler: reject }),
      ).then((worker) => resolve(worker as TesseractWorker), reject);
    }).catch((error) => {
      workerPromise = null;
      throw error;
    });
  }
  return workerPromise;
}

export async function recognizePriceTagText(
  canvas: HTMLCanvasElement,
  onDetails?: (details: PriceTagOcrDetails) => void,
): Promise<string> {
  if (canvas.width < 8 || canvas.height < 8) return "";
  const worker = await getWorker();
  const result = await worker.recognize(canvas);
  onDetails?.({
    text: result.data.text ?? "",
    confidence: result.data.confidence,
    // Tesseract's full result has circular references; retain only useful fields.
    words: result.data.words?.map(({ text, confidence, bbox }) => ({ text, confidence, bbox })),
  });
  return (result.data.text ?? "").trim();
}

export function resetPriceTagOcrForTests(): void {
  workerPromise = null;
}
